import { Menu, Pagination, Table, Text, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import * as React from "react";
import { searchItemSerials } from "../api/saleOutwardApi";

const PAGE_SIZE = 10;


const HEAD_CELL: React.CSSProperties = {
  height: 36,
  padding: "0 10px",
  background: "#F6F7F4",
  color: "#5C6570",
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #ECEEE9",
};

const BODY_CELL: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: 12.5,
  color: "#1B1F24",
  borderBottom: "1px solid #F0F1EE",
};

interface ItemSearchMenuProps {
  keyword: string;
  onKeywordChange: (keyword: string) => void;
  sourceCode: string;
  sourceType: string;
  disabled: boolean;
  /** Serial đã có trên phiếu, để đánh dấu "Đã chọn" */
  scannedSerials: Set<string>;
  onPick: (serial: string, positionId: number) => void;
  /** Tăng mỗi lần bấm nút trên header: đóng bảng kết quả */
  closeSignal: number;
  /** Tăng mỗi lần chọn mã ở bảng Thông tin đơn hàng: mở lại bảng, kể cả chọn lại đúng mã đang tìm */
  openSignal: number;
}

/** Tìm serial tồn của các mã hàng trong đơn theo từ khoá, bấm "Chọn" để thêm vào phiếu */
const ItemSearchMenu = ({
  keyword,
  onKeywordChange,
  sourceCode,
  sourceType,
  disabled,
  scannedSerials,
  onPick,
  closeSignal,
  openSignal,
}: ItemSearchMenuProps) => {
  const [debounced] = useDebouncedValue(keyword, 300);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [items, setItems] = React.useState<any[]>([]);
  const [focused, setFocused] = React.useState(false);
  const requestRef = React.useRef(0);
  // Bấm nút trên header thì đóng bảng; từ khoá đổi, chọn mã ở bảng đơn hàng hoặc bấm lại vào ô tìm thì mở lại
  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    if (closeSignal) setDismissed(true);
  }, [closeSignal]);
  React.useEffect(() => setDismissed(false), [keyword, openSignal]);

  React.useEffect(() => setPage(1), [debounced]);

  React.useEffect(() => {
    if (debounced.length <= 2 || !sourceCode || !sourceType) {
      setItems([]);
      return;
    }
    const requestId = ++requestRef.current;
    searchItemSerials({
      keySearch: debounced,
      sourceCode,
      type: sourceType,
      skip: (page - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    })
      .then((res: any) => {
        if (requestId !== requestRef.current) return;
        setItems(res?.data || []);
        setTotalPages(res?.totalCount ? Math.ceil(res.totalCount / PAGE_SIZE) : 1);
      })
      .catch(() => {
        if (requestId === requestRef.current) setItems([]);
      });
  }, [debounced, page, sourceCode, sourceType]);

  const active = focused && !disabled;

  return (
    <Menu
      opened={debounced.length > 2 && items.length > 0 && !dismissed}
      trapFocus={false}
      shadow="md"
      width="900px"
      position="bottom-start"
      offset={8}
      styles={{ dropdown: { padding: 0, border: "1px solid #DDE0DA", borderRadius: 12, overflow: "hidden" } }}
    >
      <Menu.Target>
        <label
          style={{
            width: 220,
            height: 38,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 10px",
            border: `1px solid ${active ? "#1F5FAE" : "#D5D9D2"}`,
            borderRadius: 8,
            boxShadow: active ? "0 0 0 3px #E3ECF7" : "none",
            background: disabled ? "#F6F7F4" : "#FFFFFF",
            cursor: disabled ? "not-allowed" : "text",
            flexShrink: 0,
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={disabled ? "#A3AAB2" : "#5C6570"}
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-4-4" />
          </svg>
          <input
            type="text"
            aria-label="Tìm kiếm mã sản phẩm"
            placeholder="Tìm kiếm mã sản phẩm"
            autoComplete="off"
            value={keyword}
            disabled={disabled}
            onFocus={() => {
              setFocused(true);
              setDismissed(false);
            }}
            onClick={() => setDismissed(false)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setDismissed(true);
            }}
            onChange={(e) => onKeywordChange(e.currentTarget.value ?? "")}
            style={{
              flexGrow: 1,
              minWidth: 0,
              border: 0,
              outline: "none",
              padding: 0,
              font: "inherit",
              fontSize: 13,
              color: disabled ? "#8A929B" : "#1B1F24",
              background: "transparent",
              cursor: "inherit",
            }}
          />
        </label>
      </Menu.Target>
      <Menu.Dropdown>
        <Table.ScrollContainer minWidth={500} h={500} type="native">
          <Table w="100%" highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={HEAD_CELL} />
                <Table.Th style={HEAD_CELL}>Mã vị trí</Table.Th>
                <Table.Th style={HEAD_CELL} w={120}>
                  Tên vị trí
                </Table.Th>
                <Table.Th style={HEAD_CELL} w={60}>
                  Mã sản phẩm
                </Table.Th>
                <Table.Th style={HEAD_CELL}>Tên sản phẩm</Table.Th>
                <Table.Th style={HEAD_CELL}>Số serial</Table.Th>
                <Table.Th style={HEAD_CELL}>Tuổi tồn</Table.Th>
                <Table.Th style={HEAD_CELL}>Kho</Table.Th>
                <Table.Th style={HEAD_CELL}>Đơn vị tính</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item) => {
                const picked = scannedSerials.has(String(item.serial ?? "").toUpperCase());
                return (
                  <Table.Tr key={`${item.serial}-${item.positionId}`} bg={picked ? "#F2F7FD" : undefined}>
                    <Table.Td style={BODY_CELL}>
                      <button
                        type="button"
                        disabled={picked}
                        onClick={() => onPick(item.serial, Number(item.positionId))}
                        style={{
                          height: 28,
                          padding: "0 10px",
                          border: `1px solid ${picked ? "#C9D6E8" : "#1F5FAE"}`,
                          borderRadius: 6,
                          background: picked ? "#E6EEF8" : "#1F5FAE",
                          color: picked ? "#1F5FAE" : "#FFFFFF",
                          font: "inherit",
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          cursor: picked ? "default" : "pointer",
                        }}
                      >
                        {picked ? "Đã chọn" : "Chọn"}
                      </button>
                    </Table.Td>
                    <Table.Td style={{ ...BODY_CELL, color: "#5C6570" }}>{item.positionCode}</Table.Td>
                    <Table.Td style={{ ...BODY_CELL, color: "#5C6570" }}>{item.positionName}</Table.Td>
                    <Table.Td style={BODY_CELL}>{item.itemCode}</Table.Td>
                    <Table.Td style={BODY_CELL}>
                      <Tooltip label={item.itemName}>
                        <Text w={250} lineClamp={2} style={{ fontSize: 12.5 }}>
                          {item.itemName}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td style={{ ...BODY_CELL, fontWeight: 500 }}>{item.serial}</Table.Td>
                    <Table.Td style={BODY_CELL}>{item.itemAge}</Table.Td>
                    <Table.Td style={BODY_CELL}>
                      <Text w={125} truncate="end" style={{ fontSize: 12.5 }}>
                        {item.invName}
                      </Text>
                    </Table.Td>
                    <Table.Td style={BODY_CELL}>{item.primaryUomName || item.primaryUomCode}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "8px 14px",
            borderTop: "1px solid #ECEEE9",
            background: "#FAFBF8",
          }}
        >
          <Pagination total={totalPages} value={page} withEdges onChange={setPage} size="sm" color="#1F5FAE" />
        </div>
      </Menu.Dropdown>
    </Menu>
  );
};

export default ItemSearchMenu;
